import React, { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import {
  ArrowLeft, GitBranch, RotateCcw, GitCompare, ArrowUp, ArrowDown, Plus, Minus, Pencil, Loader2,
} from 'lucide-react';
import { motion } from 'framer-motion';
import api from '../lib/api';
import { fmtMoney, fmtRelative } from '../lib/format';
import { StatusPill } from '../components/StatusPill';
import { SectionTitle, EmptyState } from '../components/common';
import { Button } from '../components/ui/button';
import { cn } from '../lib/utils';

export default function Revisions() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [project, setProject] = useState(null);
  const [revs, setRevs] = useState([]);
  const [sel, setSel] = useState({ a: null, b: null });
  const [diff, setDiff] = useState(null);
  const [loadingDiff, setLoadingDiff] = useState(false);

  const load = useCallback(async () => {
    const [p, r] = await Promise.all([api.get(`/projects/${id}`), api.get(`/projects/${id}/revisions`)]);
    setProject(p.data.project);
    setRevs(r.data);
    if (r.data.length >= 2) setSel({ a: r.data[r.data.length - 2].revNo, b: r.data[r.data.length - 1].revNo });
    else if (r.data.length === 1) setSel({ a: r.data[0].revNo, b: r.data[0].revNo });
  }, [id]);
  useEffect(() => { load(); }, [load]);

  const runDiff = useCallback(async () => {
    if (!sel.a || !sel.b) return;
    setLoadingDiff(true);
    try { const { data } = await api.get(`/projects/${id}/revisions/diff`, { params: { a: sel.a, b: sel.b } }); setDiff(data); }
    catch { setDiff(null); } finally { setLoadingDiff(false); }
  }, [id, sel]);
  useEffect(() => { runDiff(); }, [runDiff]);

  const restore = async (revNo) => {
    if (!window.confirm(`Restore Rev ${revNo}? This creates a NEW revision from that snapshot (history is preserved).`)) return;
    try { const { data } = await api.post(`/projects/${id}/revisions/${revNo}/restore`); toast.success(`Restored as Rev ${data.revNo}`); load(); }
    catch { toast.error('Restore failed'); }
  };

  if (!project) return <div className="grid h-[60vh] place-items-center"><Loader2 className="animate-spin text-muted-foreground" /></div>;

  const cur = project.currency;
  const fmtAED = (v, d = 2) => fmtMoney(v, cur, d);

  return (
    <div className="space-y-6 p-4 md:p-6 lg:p-8">
      <div className="flex flex-wrap items-center gap-3">
        <button onClick={() => navigate(`/projects/${id}`)} className="grid h-8 w-8 place-items-center rounded-lg border hover:bg-muted"><ArrowLeft size={15} /></button>
        <div>
          <h1 className="font-display text-2xl font-bold">Revisions</h1>
          <div className="flex items-center gap-2 text-sm text-muted-foreground"><span className="font-num text-[hsl(var(--copper))]">{project.code}</span>{project.name}<StatusPill status={project.status} /></div>
        </div>
      </div>

      {revs.length === 0 ? (
        <EmptyState icon={GitBranch} title="No revisions yet" description="Create Rev 1 in the workspace to lock pricing and start tracking changes."
          action={<Button onClick={() => navigate(`/projects/${id}`)}>Open Workspace</Button>} testid="revisions-empty" />
      ) : (
        <div className="grid gap-6 lg:grid-cols-12">
          {/* Timeline */}
          <div className="lg:col-span-5">
            <SectionTitle className="mb-3">Timeline</SectionTitle>
            <div className="relative space-y-3 pl-6">
              <div className="absolute bottom-2 left-2 top-2 w-px bg-border" />
              {[...revs].reverse().map((r) => {
                const isA = sel.a === r.revNo, isB = sel.b === r.revNo;
                return (
                  <motion.div key={r.revNo} initial={{ opacity: 0, x: -8 }} animate={{ opacity: 1, x: 0 }}
                    className={cn('relative erak-card p-4', (isA || isB) && 'ring-1 ring-[hsl(var(--teal)/0.4)]')}>
                    <span className="absolute -left-[18px] top-5 grid h-3 w-3 place-items-center rounded-full border-2 border-background bg-[hsl(var(--copper))]" />
                    <div className="flex items-start justify-between">
                      <div>
                        <div className="flex items-center gap-2">
                          <span className="font-num text-sm font-semibold">Rev {r.revNo}</span>
                          <span className="text-xs text-muted-foreground">{r.label}</span>
                        </div>
                        <div className="text-[11px] text-muted-foreground">{r.createdBy} · {fmtRelative(r.createdAt)}</div>
                        {r.note && <div className="mt-1 text-xs text-muted-foreground">{r.note}</div>}
                      </div>
                      <div className="text-right">
                        <div className="money text-sm font-semibold">{fmtAED(r.quotationPrice, 0)}</div>
                        {r.deltaValue !== 0 && (
                          <div className={cn('flex items-center justify-end gap-0.5 text-[11px]', r.deltaValue > 0 ? 'text-[hsl(var(--success))]' : 'text-[hsl(var(--danger))]')}>
                            {r.deltaValue > 0 ? <ArrowUp size={11} /> : <ArrowDown size={11} />}{fmtAED(Math.abs(r.deltaValue), 0)} ({r.deltaPct > 0 ? '+' : ''}{r.deltaPct}%)
                          </div>
                        )}
                      </div>
                    </div>
                    <div className="mt-3 flex items-center gap-2">
                      <button onClick={() => setSel((s) => ({ ...s, a: r.revNo }))} className={cn('rounded-lg border px-2 py-1 text-[11px]', isA ? 'border-[hsl(var(--teal))] bg-[hsl(var(--teal)/0.12)] text-[hsl(var(--teal))]' : 'hover:bg-muted')}>Compare A</button>
                      <button onClick={() => setSel((s) => ({ ...s, b: r.revNo }))} className={cn('rounded-lg border px-2 py-1 text-[11px]', isB ? 'border-[hsl(var(--copper))] bg-[hsl(var(--copper)/0.12)] text-[hsl(var(--copper))]' : 'hover:bg-muted')}>Compare B</button>
                      <button data-testid={`restore-rev-${r.revNo}`} onClick={() => restore(r.revNo)} className="ml-auto flex items-center gap-1 rounded-lg border px-2 py-1 text-[11px] hover:bg-muted"><RotateCcw size={11} />Restore</button>
                    </div>
                  </motion.div>
                );
              })}
            </div>
          </div>

          {/* Diff */}
          <div className="lg:col-span-7">
            <div className="mb-3 flex items-center justify-between">
              <SectionTitle>Compare Rev {sel.a} → Rev {sel.b}</SectionTitle>
              <GitCompare size={16} className="text-[hsl(var(--teal))]" />
            </div>
            {loadingDiff ? (
              <div className="erak-card grid h-40 place-items-center"><Loader2 className="animate-spin text-muted-foreground" /></div>
            ) : diff ? (
              <div className="space-y-4">
                <div className="erak-card p-4">
                  <p className="text-sm" data-testid="diff-summary">{diff.summary}</p>
                  <div className="mt-3 flex gap-3 text-xs">
                    <span className="flex items-center gap-1 text-[hsl(var(--success))]"><Plus size={12} />{diff.counts.added} added</span>
                    <span className="flex items-center gap-1 text-[hsl(var(--warning))]"><Pencil size={12} />{diff.counts.changed} changed</span>
                    <span className="flex items-center gap-1 text-[hsl(var(--danger))]"><Minus size={12} />{diff.counts.removed} removed</span>
                  </div>
                </div>
                <div className="erak-card overflow-hidden">
                  {diff.lineChanges.length === 0 ? (
                    <div className="p-8 text-center text-sm text-muted-foreground">No line-level changes between these revisions.</div>
                  ) : (
                    <table className="w-full text-xs">
                      <thead><tr className="border-b bg-muted/40 text-left text-[10px] uppercase tracking-wide text-muted-foreground">
                        <th className="px-3 py-2">Change</th><th className="px-3 py-2">Item</th><th className="px-3 py-2">Category</th>
                        <th className="px-3 py-2">Line</th><th className="px-3 py-2 text-right">Old</th><th className="px-3 py-2 text-right">New</th>
                      </tr></thead>
                      <tbody>
                        {diff.lineChanges.map((c, i) => (
                          <tr key={`${c.itemNo}-${c.category}-${c.code || c.description || ''}-${c.type}-${i}`} className={cn('border-b last:border-0',
                            c.type === 'added' && 'bg-[hsl(var(--success)/0.08)]',
                            c.type === 'removed' && 'bg-[hsl(var(--danger)/0.08)]',
                            c.type === 'changed' && 'bg-[hsl(var(--warning)/0.08)]')}>
                            <td className="px-3 py-2">
                              <span className={cn('rounded-full px-1.5 py-0.5 text-[10px] font-medium capitalize',
                                c.type === 'added' && 'bg-[hsl(var(--success)/0.2)] text-[hsl(var(--success))]',
                                c.type === 'removed' && 'bg-[hsl(var(--danger)/0.2)] text-[hsl(var(--danger))]',
                                c.type === 'changed' && 'bg-[hsl(var(--warning)/0.2)] text-[hsl(var(--warning))]')}>{c.type}</span>
                            </td>
                            <td className="px-3 py-2 font-num text-[hsl(var(--copper))]">{c.itemNo}</td>
                            <td className="px-3 py-2 text-muted-foreground">{c.category}</td>
                            <td className="px-3 py-2"><span className="font-num">{c.code || ''}</span> {c.description}</td>
                            <td className="money px-3 py-2 text-muted-foreground">{c.oldAmount != null ? fmtAED(c.oldAmount) : '—'}</td>
                            <td className="money px-3 py-2 font-medium">{c.newAmount != null ? fmtAED(c.newAmount) : '—'}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  )}
                </div>
              </div>
            ) : (
              <div className="erak-card p-8 text-center text-sm text-muted-foreground">Select two revisions (A and B) to compare.</div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
